import { ApexOptions } from 'apexcharts';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { ChartRendererProps, ChartSize } from '../../types/chart-renderer-props';
import { HeatmapCellClickPayload, resolveHeatmapCellClick } from '../../utils/resolve-heatmap-cell-click';
import {
    HOUR_DAY_HEATMAP_DAYS,
    HOUR_DAY_HEATMAP_HOURS,
    HourDayHeatmapModel,
    HourDayHeatmapSeries,
    deriveHourDayHeatmapRangeStart,
    toHourDayHeatmapModel
} from '../../utils/to-hour-day-heatmap-series';
import { WeekdayWeekHeatmapSeries } from '../../utils/to-weekday-week-heatmap-series';
import { ChartContainer } from '../shared/chart-container.component';
import { formatAxisLabel } from '../shared/format-axis-label';
import { getWeekdayWeekHeatmapChartOptions } from '../weekday-week-heatmap-chart/weekday-week-heatmap-chart-options';

export interface HourDayHeatmapChartProps extends ChartRendererProps {
    rangeStart?: string;
    dayCount?: number;
    /** When set, non-empty cells are clickable and invoke this with cell date/hour/value. */
    onCellClick?: (cell: HeatmapCellClickPayload) => void;
}

const toApexHeatmapSeries = (series: HourDayHeatmapSeries[]): ApexOptions['series'] =>
    series.map(({ name, data }) => ({
        name: formatAxisLabel(name?.trim() || '') || 'Day',
        data: data.map(({ y }) => y)
    }));

interface HourDayHeatmapChartViewProps {
    heatmapModel: HourDayHeatmapModel;
    apexSeries: ApexOptions['series'];
    size: ChartSize;
    chartOptions?: ApexOptions;
    onCellClick?: (cell: HeatmapCellClickPayload) => void;
}

const HourDayHeatmapChartView = ({ heatmapModel, apexSeries, size, chartOptions, onCellClick }: HourDayHeatmapChartViewProps) => {
    const options = useMemo(() => {
        const base = getWeekdayWeekHeatmapChartOptions({
            height: size.height,
            width: size.width,
            maxValue: heatmapModel.maxValue,
            weekLabels: heatmapModel.hourLabels,
            tooltipSeries: heatmapModel.series as unknown as WeekdayWeekHeatmapSeries[]
        });

        const withAxis = {
            ...base,
            xaxis: {
                ...base.xaxis,
                labels: {
                    ...base.xaxis?.labels,
                    rotate: -45,
                    formatter: (value: string) => String(value ?? '')
                }
            },
            ...chartOptions
        };

        if (!onCellClick) {
            return withAxis;
        }

        return {
            ...withAxis,
            chart: {
                ...base.chart,
                ...chartOptions?.chart,
                events: {
                    ...base.chart?.events,
                    ...chartOptions?.chart?.events,
                    dataPointSelection: (event, chartContext, config) => {
                        event?.preventDefault?.();
                        event?.stopPropagation?.();
                        chartOptions?.chart?.events?.dataPointSelection?.(event, chartContext, config);
                        const cell = resolveHeatmapCellClick(heatmapModel.series, config.seriesIndex, config.dataPointIndex);
                        if (cell) {
                            onCellClick(cell);
                        }
                    }
                }
            }
        };
    }, [chartOptions, heatmapModel.hourLabels, heatmapModel.maxValue, heatmapModel.series, onCellClick, size.height, size.width]);

    return (
        <ReactApexChart
            key={`${heatmapModel.maxValue}-${heatmapModel.hourLabels.length}`}
            options={options}
            series={apexSeries}
            type='heatmap'
            height={size.height}
            width={size.width}
        />
    );
};

export const HourDayHeatmapChart = ({
    data,
    className,
    minHeight = 280,
    chartOptions,
    rangeStart,
    dayCount = HOUR_DAY_HEATMAP_DAYS,
    onCellClick
}: HourDayHeatmapChartProps) => {
    const hourLabels = useMemo(() => Array.from({ length: HOUR_DAY_HEATMAP_HOURS }, (_, hour) => `${String(hour).padStart(2, '0')}:00`), []);

    const heatmapModel = useMemo(() => {
        const parsedRangeStart = rangeStart ? new Date(rangeStart) : deriveHourDayHeatmapRangeStart(data);
        if (!parsedRangeStart || Number.isNaN(parsedRangeStart.getTime())) {
            return null;
        }

        return toHourDayHeatmapModel(data, parsedRangeStart, dayCount, hourLabels);
    }, [data, dayCount, hourLabels, rangeStart]);

    const apexSeries = useMemo(() => (heatmapModel ? toApexHeatmapSeries(heatmapModel.series) : []), [heatmapModel]);

    if (!heatmapModel || !apexSeries?.length) {
        return null;
    }

    const containerClassName = `${className ?? ''}${onCellClick ? ' cursor-pointer' : ''}`.trim();

    return (
        <ChartContainer
            className={containerClassName || undefined}
            minHeight={minHeight}>
            {(size) => (
                <HourDayHeatmapChartView
                    heatmapModel={heatmapModel}
                    apexSeries={apexSeries}
                    size={size}
                    chartOptions={chartOptions}
                    onCellClick={onCellClick}
                />
            )}
        </ChartContainer>
    );
};
